import {useEffect,useMemo,useState} from 'react';
import {Mail} from 'lucide-react';
import {api,send,ApiError} from '../../api';
import {Button} from '../ui/button';
import type {EmailSignatureView,SignatureInput} from '../../../../src/application/email-signature';
import './signature.css';

const blank:SignatureInput={senderName:'',senderTitle:'',company:'',billingEmail:'',websiteUrl:'',bookingUrl:'',logoUrl:'',
 statementLinks:'sequence_portal',tone:'friendly',toneNote:''};
const https=(value:string)=>{
 try{const url=new URL(value.trim());return url.protocol==='https:'&&!url.username&&!url.password?url.toString():null;}
 catch{return null;}};
const label=(url:string)=>url.replace(/^https:\/\//,'').replace(/\/$/,'');
const fields:{key:keyof SignatureInput;label:string;hint?:string;type?:string}[]=[
 {key:'senderName',label:'What do you call your agent?',hint:'Used on emails, calls and across Alder, e.g. Carol or Amy.'},{key:'senderTitle',label:'Agent title'},
 {key:'company',label:'Company name',hint:'Spelled exactly as customers should read it'},
 {key:'billingEmail',label:'Billing contact email',hint:'Named in "please reach out to ..."; blank uses the sender mailbox',type:'email'},
 {key:'websiteUrl',label:'Website',hint:'https://...'},{key:'bookingUrl',label:'Booking link',hint:'Linked as "Book a meeting"'},
 {key:'logoUrl',label:'Logo image',hint:'https:// image, shown at most 32px tall'}];

/** A sample reminder with the signature as customers will see it. The real wording is written per email. */
function Preview({value,mailbox}:{value:SignatureInput;mailbox:string}){
 const booking=https(value.bookingUrl);const website=https(value.websiteUrl);const logo=https(value.logoUrl);
 const linked=value.statementLinks==='sequence_portal';
 const reminder=value.tone==='firm'?'This is a reminder that your invoice from August is outstanding on your account.'
  :value.tone==='neutral'?'This is a reminder that your invoice from August is outstanding.'
  :'This is a friendly reminder that your invoice from August is outstanding on your account.';
 return <div className="sig-preview-panel">
  <div className="sig-preview" aria-label="Agent identity preview">
   <p>To Example Customer Team,</p>{value.tone!=='neutral'&&<p>{value.tone==='firm'?'Hello,':'We hope you&apos;re doing well!'}</p>}
   <p>{reminder} Please review {linked
    ?<>your current statement <a href="#preview" onClick={e=>e.preventDefault()}>here</a></>:'your account'} and settle the outstanding
    balance{value.tone==='firm'?' promptly':' at your earliest convenience'}.</p>
   <p>If you have any questions, please don&apos;t hesitate to reach out to {value.billingEmail||mailbox}.</p>
   <p>Best,<br/>{value.senderName||'Sender name'}<br/>{[value.senderTitle||'Title',value.company||'Company'].join(' | ')}
    {booking&&<> | <a href={booking} target="_blank" rel="noreferrer">Book a meeting</a></>}
    {website&&<><br/><a href={website} target="_blank" rel="noreferrer">{label(website)}</a></>}
    {logo&&<><br/><img src={logo} alt={value.company} className="sig-logo"/></>}</p>
  </div>
  {value.toneNote&&<p className="sig-tone-note"><strong>Tone note:</strong> {value.toneNote} — guidance for new drafts, not part of the email.</p>}
 </div>;
}

/**
 * Agent identity for customer email and calls: an owner edits the name, title, signature links and tone here, everyone
 * else reads them. Standalone (its own API calls), so it can sit on the Policy page or in the Integrations page slot.
 */
export function EmailSignatureCard({owner}:{owner:boolean}){
 const [stored,setStored]=useState<EmailSignatureView|null>(null);const [value,setValue]=useState<SignatureInput>(blank);
 const [error,setError]=useState('');const [saved,setSaved]=useState('');const [saving,setSaving]=useState(false);
 useEffect(()=>{void api<EmailSignatureView>('/app-api/settings/email-signature').then(view=>{setStored(view);
  const {mailbox:_m,revision:_r,preview:_p,...input}=view;void _m;void _r;void _p;setValue(input);})
  .catch(e=>setError(e instanceof ApiError&&e.status===404?'No sender is configured on this host yet.'
   :'The signature could not be loaded.'));},[]);
 const invalid=useMemo(()=>(['websiteUrl','bookingUrl','logoUrl'] as const).filter(k=>value[k].trim()&&!https(value[k])),[value]);
 const dirty=stored!==null&&(fields.some(f=>value[f.key]!==stored[f.key])||value.statementLinks!==stored.statementLinks
  ||value.tone!==stored.tone||value.toneNote!==stored.toneNote);
 const save=async()=>{setSaving(true);setError('');setSaved('');
  try{const view=await send<EmailSignatureView>('/app-api/settings/email-signature','PUT',{...value,baseRevision:stored?.revision});
   setStored(view);setSaved('Saved. New drafts and calls use this identity.');}
  catch(e){setError(e instanceof Error?e.message:'The signature could not be saved.');}finally{setSaving(false);}};
 return <section className="pj-panel sig-card" aria-labelledby="sig-title">
  <h2 id="sig-title"><Mail size={15} aria-hidden/> Agent identity</h2>
  <p className="sig-lede">Who the agent is to your customers, in email and on calls. Drafts already waiting for approval keep
   the name, signature and wording they were written with.</p>
  {error&&<div className="alert error" role="alert">{error}</div>}
  {stored&&<div className="sig-grid">
   <form className="sig-form" onSubmit={e=>{e.preventDefault();void save();}}>
    {fields.map(f=><label key={f.key}><span>{f.label}</span>
     <input type={f.type??'text'} value={String(value[f.key])} disabled={!owner} aria-invalid={invalid.includes(f.key as never)||undefined}
      onChange={e=>setValue({...value,[f.key]:e.target.value})}/>
     {f.hint&&<small>{invalid.includes(f.key as never)?'Use a plain https:// address':f.hint}</small>}</label>)}
    <label><span>Statement link</span>
     <select value={value.statementLinks} disabled={!owner}
      onChange={e=>setValue({...value,statementLinks:e.target.value as SignatureInput['statementLinks']})}>
      <option value="sequence_portal">Link “here” to the customer’s Sequence portal</option>
      <option value="none">No link (“Please review your account”)</option></select></label>
    <label><span>Tone</span>
     <select value={value.tone} disabled={!owner}
      onChange={e=>setValue({...value,tone:e.target.value as SignatureInput['tone']})}>
      <option value="friendly">Friendly</option><option value="neutral">Neutral</option><option value="firm">Firm</option>
     </select></label>
    <label><span>Tone note (optional)</span>
     <input value={value.toneNote} disabled={!owner} maxLength={280}
      onChange={e=>setValue({...value,toneNote:e.target.value})}/>
     <small>Plain guidance for new drafts, such as “keep it brief”. The policy still sets each reminder&apos;s escalation.</small></label>
    <p className="sig-mailbox">Sends from {stored.mailbox}</p>
    {owner&&<div className="sig-actions"><Button type="submit" disabled={!dirty||saving||invalid.length>0}>
     {saving?'Saving…':'Save identity'}</Button>{saved&&<span role="status">{saved}</span>}</div>}
   </form>
   <Preview value={value} mailbox={stored.mailbox}/>
  </div>}
  {!owner&&<p className="muted sig-lede">An owner can change the agent&apos;s identity.</p>}
 </section>;
}
